import type { CallCardSettings, EntityType } from '../shared/settings.js';
import { entityTypes, normalizeSettings } from '../shared/settings.js';
import {
  apiFetch,
  completeFrameSession,
  escapeHtml,
  getBootstrap,
  initBitrix,
  resizeFrame,
  showFatal
} from './core.js';

interface FieldDefinition {
  title?: string;
  formLabel?: string;
  listLabel?: string;
  filterLabel?: string;
  type?: string;
}

interface SettingsResponse {
  settings: CallCardSettings;
  migratedFromAppOption: boolean;
  updatedAt: string;
}

type FieldMaps = Record<EntityType, Record<string, FieldDefinition>>;

const ENTITY_CAPTIONS: Record<EntityType, string> = {
  lead: 'Лиды',
  contact: 'Контакты',
  company: 'Компании',
  deal: 'Сделки'
};

const PRESETS: Record<string, Record<EntityType, string[]>> = {
  minimal: {
    lead: ['TITLE', 'NAME', 'LAST_NAME', 'PHONE', 'EMAIL', 'STATUS_ID', 'STAGE_ID', 'ASSIGNED_BY_ID'],
    contact: ['NAME', 'LAST_NAME', 'PHONE', 'EMAIL', 'ASSIGNED_BY_ID'],
    company: ['TITLE', 'COMPANY_TITLE', 'PHONE', 'EMAIL', 'WEB', 'ASSIGNED_BY_ID'],
    deal: ['TITLE', 'OPPORTUNITY', 'CATEGORY_ID', 'STAGE_ID', 'ASSIGNED_BY_ID']
  },
  sales: {
    lead: ['TITLE', 'OPPORTUNITY', 'SOURCE_ID', 'SOURCE_DESCRIPTION', 'STATUS_ID', 'ASSIGNED_BY_ID', 'PHONE', 'EMAIL', 'COMMENTS'],
    contact: ['NAME', 'LAST_NAME', 'POST', 'COMPANY_TITLE', 'COMPANY_ID', 'ASSIGNED_BY_ID', 'PHONE', 'EMAIL', 'COMMENTS'],
    company: ['TITLE', 'COMPANY_TITLE', 'REVENUE', 'INDUSTRY', 'ASSIGNED_BY_ID', 'PHONE', 'EMAIL', 'WEB', 'COMMENTS'],
    deal: ['TITLE', 'OPPORTUNITY', 'CURRENCY_ID', 'CATEGORY_ID', 'STAGE_ID', 'PROBABILITY', 'SOURCE_ID', 'ASSIGNED_BY_ID', 'COMMENTS', 'CONTACT_ID', 'COMPANY_ID']
  },
  contact_center: {
    lead: ['NAME', 'LAST_NAME', 'PHONE', 'EMAIL', 'IM', 'WEB', 'COMMENTS', 'ASSIGNED_BY_ID'],
    contact: ['NAME', 'LAST_NAME', 'PHONE', 'EMAIL', 'IM', 'WEB', 'COMMENTS', 'ASSIGNED_BY_ID'],
    company: ['TITLE', 'COMPANY_TITLE', 'PHONE', 'EMAIL', 'WEB', 'ADDRESS', 'COMMENTS', 'ASSIGNED_BY_ID'],
    deal: ['TITLE', 'CONTACT_ID', 'COMPANY_ID', 'PHONE', 'EMAIL', 'COMMENTS', 'ASSIGNED_BY_ID']
  }
};

const PRESET_DESCRIPTIONS: Record<EntityType, Record<string, string>> = {
  lead: {
    minimal: 'название лида, контактные данные, текущий статус или этап и ответственный.',
    sales: 'сумма, стадия, источник, ответственный и контактные поля лида.',
    contact_center: 'телефон, email, комментарии и быстрые данные для обработки обращения по лиду.'
  },
  contact: {
    minimal: 'имя, фамилия, основной телефон, email и ответственный.',
    sales: 'ФИО, компания, должность, ответственный и ключевые контактные поля.',
    contact_center: 'телефон, email, мессенджеры и комментарии для быстрого контакта с клиентом.'
  },
  company: {
    minimal: 'название компании, основной телефон, email и ответственный.',
    sales: 'название компании, оборот или сумма, источник, ответственный и контакты.',
    contact_center: 'телефоны, email, сайт, адрес и комментарии для связи с компанией.'
  },
  deal: {
    minimal: 'название сделки, сумма, стадия и ответственный.',
    sales: 'сумма, валюта, стадия, источник, ответственный и связанные контакты.',
    contact_center: 'контакты сделки, компания, комментарии и данные для быстрого сопровождения клиента.'
  }
};

let bootstrap = getBootstrap();
const root = document.getElementById('app');
let draft: CallCardSettings | null = null;
let fieldMaps: FieldMaps | null = null;
let activeEntity: EntityType = 'lead';
let searchValue = '';
let saving = false;
let showExtraDescriptions = false;
let showSystemFields = false;
let showSelectedOnly = false;
let drawerOpen = false;
let mobileSidebarOpen = false;
let activePreset = '';
let savedSnapshot = '';

if (!root) {
  showFatal('Сессия не найдена. Откройте приложение из Битрикс24.');
} else {
  void ensureSessionAndBoot();
}

async function ensureSessionAndBoot(): Promise<void> {
  try {
    if (!bootstrap) {
      bootstrap = await completeFrameSession('/app/complete');
    }
    await boot();
  } catch (error) {
    showFatal(error instanceof Error ? error.message : 'Не удалось открыть приложение');
  }
}

async function boot(): Promise<void> {
  renderLoading();
  try {
    const settingsResponse = await apiFetch<SettingsResponse>('/api/v1/settings');
    draft = normalizeSettings(settingsResponse.settings);

    if (isLocalPreview()) {
      fieldMaps = createLocalFieldMaps(draft);
    } else if (bootstrap?.isAdmin) {
      await migrateLegacySettings(settingsResponse);
      fieldMaps = await apiFetch<FieldMaps>('/api/v1/crm/fields');
    }

    savedSnapshot = snapshot(draft);
    render();
    bindEvents();
    resizeFrame();
  } catch (error) {
    showFatal(error instanceof Error ? error.message : 'Не удалось загрузить настройки');
  }
}

async function migrateLegacySettings(response: SettingsResponse): Promise<void> {
  if (response.migratedFromAppOption) return;
  try {
    const bx24 = await initBitrix();
    const legacyValue = bx24.appOption?.get('call_card_widget_settings_v1');
    if (!legacyValue) return;
    const migrated = await apiFetch<{ settings: CallCardSettings }>(
      '/api/v1/settings/migrate',
      { method: 'POST', body: JSON.stringify({ value: legacyValue }) }
    );
    draft = normalizeSettings(migrated.settings);
  } catch {
    // Миграция повторится при следующем открытии; серверные настройки остаются рабочими.
  }
}

function renderLoading(): void {
  if (!root) return;
  root.innerHTML = `
    <div class="app-layout app-shell">
      <main class="main-content">
        <section class="settings-container section">
          <h2>Настройки карточки звонка</h2>
          <p>Загружаем настройки и список полей CRM…</p>
        </section>
      </main>
    </div>`;
}

function render(): void {
  if (!root || !draft || !bootstrap) return;
  const descriptionsClass = showExtraDescriptions ? '' : ' helper-text-hidden';
  const dirtyClass = isDirty() ? ' is-visible' : '';
  const previewNotice = isLocalPreview()
    ? '<div class="notification notification--info">Локальный предпросмотр: значения полей клиента появятся только при открытии карточки из Битрикс24.</div>'
    : bootstrap.isAdmin
      ? ''
      : '<div class="notification notification--info">Настройки доступны только администратору. Текущая конфигурация открыта для просмотра.</div>';

  root.innerHTML = `
    <div class="app-layout app-shell">
      <div class="sidebar-overlay" data-action="close-sidebar" aria-hidden="true"></div>
      ${renderSidebar()}
      <main class="main-content">
        <header class="top-bar">
          <button class="hamburger" id="sidebar-toggle" type="button" aria-label="Открыть меню" aria-expanded="${mobileSidebarOpen}" data-action="toggle-sidebar">
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M4 7h16"></path><path d="M4 12h16"></path><path d="M4 17h16"></path>
            </svg>
          </button>
          <div>
            <h1 class="page-title">Настройки виджета карточки звонка</h1>
            <p class="page-subtitle">Выберите поля CRM, правила показа и визуально проверьте состав карточки звонка.</p>
          </div>
        </header>
        <div class="page-divider"></div>
        <div class="notification-area" id="notice">${previewNotice}</div>

        <section class="settings-container section" id="overview">
          <h2>Общие настройки</h2>
          <p>Выберите сущность, отметьте нужные поля и проверьте результат в превью.</p>
          <div class="tabs ui-tabs" role="tablist">
            ${entityTypes.map(renderEntityTab).join('')}
          </div>
          <div class="helpers-toggle-row">
            <label class="feature-toggle">
              <input type="checkbox" id="show-extra-descriptions" ${showExtraDescriptions ? 'checked' : ''}>
              Показывать пояснения
            </label>
          </div>
          <div class="preset-row">
            <span class="preset-label">Пресеты:</span>
            ${renderPreset('minimal', 'Минимум')}
            ${renderPreset('sales', 'Продажи')}
            ${renderPreset('contact_center', 'Контакт-центр')}
          </div>
          <div class="preset-help helper-text${descriptionsClass}">
            Пресет применяется только к текущей вкладке. После применения нажмите «Сохранить настройки».
          </div>
          <div class="preset-descriptions helper-text${descriptionsClass}">
            <p class="preset-descriptions__item"><strong>Минимум:</strong> ${escapeHtml(PRESET_DESCRIPTIONS[activeEntity].minimal)}</p>
            <p class="preset-descriptions__item"><strong>Продажи:</strong> ${escapeHtml(PRESET_DESCRIPTIONS[activeEntity].sales)}</p>
            <p class="preset-descriptions__item"><strong>Контакт-центр:</strong> ${escapeHtml(PRESET_DESCRIPTIONS[activeEntity].contact_center)}</p>
          </div>
        </section>

        <section class="settings-container section" id="fields">
          <h2>Поля CRM</h2>
          <p>Поиск, фильтрация и настройка состава полей для каждой сущности.</p>
          <div class="settings-toolbar">
            <input id="field-search" class="ui-input" type="search" value="${escapeHtml(searchValue)}"
              placeholder="Поиск полей по названию или коду" autocomplete="off">
            <div class="toolbar-actions">
              <button class="btn btn-ghost ui-btn ui-btn-ghost" type="button" data-action="select-visible" ${disabledAttr()}>Выбрать видимые</button>
              <button class="btn btn-ghost ui-btn ui-btn-ghost" type="button" data-action="clear-visible" ${disabledAttr()}>Снять видимые</button>
            </div>
          </div>
          <div class="toolbar-help helper-text${descriptionsClass}">
            <p class="toolbar-help__item"><strong>Выбрать видимые:</strong> отметить все поля, показанные с учётом поиска и фильтров.</p>
            <p class="toolbar-help__item"><strong>Снять видимые:</strong> убрать отметку у всех полей, показанных в списке.</p>
          </div>
          <div class="system-toggle-row">
            <label class="feature-toggle">
              <input type="checkbox" id="show-system-fields" ${showSystemFields ? 'checked' : ''}>
              Показывать системные поля CRM
            </label>
          </div>
          <div class="system-toggle-row">
            <label class="feature-toggle">
              <input type="checkbox" id="show-selected-only" ${showSelectedOnly ? 'checked' : ''}>
              Показывать только выбранные поля
            </label>
          </div>
          <div class="field-filter-help helper-text${descriptionsClass}">
            <p class="field-filter-help__text">Первый фильтр управляет системными полями CRM, второй оставляет в списке только отмеченные поля.</p>
          </div>
          ${renderFields()}
        </section>

        <section class="settings-container section advanced-settings" id="rules">
          <h2>Дополнительные возможности</h2>
          <p>Эта настройка определяет, показывать ли связанные CRM-сущности вместе с основной.</p>
          <div class="advanced-grid">
            ${renderCheckbox('merge_entities', 'Объединять связанные сущности', draft.__advanced.merge_entities)}
          </div>
        </section>

        <section class="preview-panel settings-container section" id="preview">
          <h3>Превью карточки звонка</h3>
          <div class="preview-card" id="settings-preview">${renderPreview()}</div>
        </section>

        <section class="settings-container section" id="actions">
          <h2>Действия</h2>
          <p>Сохраните выбранные поля и правила показа карточки звонка.</p>
          <div class="form-actions">
            <button class="btn btn-primary ui-btn ui-btn-primary${isDirty() ? ' is-dirty' : ''}" id="save-settings" type="button" ${disabledAttr()}>
              Сохранить настройки
            </button>
            <button class="btn btn-secondary ui-btn ui-btn-light" id="reload-settings" type="button">Обновить данные</button>
          </div>
          <div class="dirty-state-row">
            <span class="dirty-state-badge${dirtyClass}">Есть несохранённые изменения</span>
          </div>
        </section>

        <section class="settings-container section additional-settings-section" id="additional-settings">
          <details class="additional-settings-panel">
            <summary class="additional-settings-panel__summary"><span>Дополнительно</span></summary>
            <div class="additional-settings-panel__content">
              <p class="additional-settings-panel__intro">Общие для портала правила показа виджета.</p>
              <div class="additional-settings-block">
                <h3>Правила показа</h3>
                <div class="advanced-grid advanced-grid--compact">
                  ${renderCheckbox('incoming_only', 'Только входящие звонки', draft.__advanced.rules.incoming_only)}
                  ${renderCheckbox('outgoing_only', 'Только исходящие звонки', draft.__advanced.rules.outgoing_only)}
                  ${renderCheckbox('worktime_only', 'Только рабочее время (9–18)', draft.__advanced.rules.worktime_only)}
                  ${renderCheckbox('weekdays_only', 'Только будни', draft.__advanced.rules.weekdays_only)}
                </div>
              </div>
            </div>
          </details>
        </section>
      </main>
    </div>`;

  document.body.classList.toggle('sidebar-open', mobileSidebarOpen);
}

function renderSidebar(): string {
  return `<aside class="sidebar" id="sidebar" aria-label="Навигация приложения">
    <nav class="menu" aria-label="Разделы настроек">
      ${renderMenuItem('overview', 'Общие настройки', 'home')}
      ${renderMenuItem('fields', 'Поля CRM', 'settings')}
      ${renderMenuItem('rules', 'Возможности', 'branding')}
      ${renderMenuItem('preview', 'Превью', 'faq')}
      ${renderMenuItem('actions', 'Действия', 'admin')}
    </nav>
    ${renderBrandMenu()}
  </aside>`;
}

function renderMenuItem(id: string, label: string, icon: 'home' | 'settings' | 'branding' | 'faq' | 'admin'): string {
  const icons = {
    home: '<path d="M3 9.5l9-6 9 6"></path><path d="M5 10.5v9h5v-6h4v6h5v-9"></path>',
    settings: '<circle cx="12" cy="12" r="3.5"></circle><path d="M19.4 15a1.8 1.8 0 0 0 .36 2l.04.04a2 2 0 1 1-2.83 2.83l-.04-.04a1.8 1.8 0 0 0-2-.36 1.8 1.8 0 0 0-1 1.66V21a2 2 0 1 1-4 0v-.06a1.8 1.8 0 0 0-1-1.66 1.8 1.8 0 0 0-2 .36l-.04.04a2 2 0 1 1-2.83-2.83l.04-.04a1.8 1.8 0 0 0 .36-2 1.8 1.8 0 0 0-1.66-1H3a2 2 0 1 1 0-4h.06a1.8 1.8 0 0 0 1.66-1 1.8 1.8 0 0 0-.36-2l-.04-.04a2 2 0 1 1 2.83-2.83l.04.04a1.8 1.8 0 0 0 2 .36 1.8 1.8 0 0 0 1-1.66V3a2 2 0 1 1 4 0v.06a1.8 1.8 0 0 0 1.66 1 1.8 1.8 0 0 0 2-.36l.04-.04a2 2 0 1 1 2.83 2.83l-.04.04a1.8 1.8 0 0 0-.36 2 1.8 1.8 0 0 0 1.66 1H21a2 2 0 1 1 0 4h-.06a1.8 1.8 0 0 0-1.66 1z"></path>',
    branding: '<path d="M4 7h10a3 3 0 0 1 0 6H4z"></path><path d="M4 13h13a3 3 0 0 1 0 6H4z"></path>',
    faq: '<circle cx="12" cy="12" r="9"></circle><path d="M9.5 9a2.5 2.5 0 1 1 3.5 2.3c-.9.4-1.3.9-1.3 1.7"></path><circle cx="12" cy="17.5" r="0.8" fill="currentColor" stroke="none"></circle>',
    admin: '<path d="M12 3l7 4v5c0 5-3.5 8-7 9-3.5-1-7-4-7-9V7l7-4z"></path>'
  } as const;
  const activeId = window.location.hash.slice(1) || 'overview';
  return `<a class="menu-item${activeId === id ? ' active' : ''}" href="#${id}" data-action="navigate-section" data-section="${id}">
    <span class="menu-icon" aria-hidden="true">
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">${icons[icon]}</svg>
    </span>
    ${escapeHtml(label)}
  </a>`;
}

function renderBrandMenu(): string {
  return `<div class="footer-menu${drawerOpen ? ' is-open' : ''}">
    <div class="footer-dropdown" id="app-drawer" aria-label="Меню приложения">
      <a class="footer-link" href="https://bazabz.ru/" target="_blank" rel="noopener noreferrer">
        <span class="footer-icon" aria-hidden="true"><svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"></circle><path d="M3 12h18"></path><path d="M12 3a15 15 0 0 1 0 18"></path><path d="M12 3a15 15 0 0 0 0 18"></path></svg></span>
        Сайт
      </a>
      <a class="footer-link" href="https://bazabz.ru/politika_konfidentsialnosti" target="_blank" rel="noopener noreferrer">
        <span class="footer-icon" aria-hidden="true"><svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l7 4v5c0 5-3.5 8-7 9-3.5-1-7-4-7-9V7l7-4z"></path><path d="M9.5 12.5l1.5 1.5 3.5-3.5"></path></svg></span>
        Политика конфиденциальности
      </a>
      <a class="footer-link" href="https://bazabz.ru/lic-sogl/kartochka-zvonka" target="_blank" rel="noopener noreferrer">
        <span class="footer-icon" aria-hidden="true"><svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4 9l4-4 4 4"></path><path d="M20 9l-4-4-4 4"></path><path d="M7 12l2 2c1 1 3 1 4 0l2-2"></path><path d="M5 11l2 2"></path><path d="M19 11l-2 2"></path></svg></span>
        Пользовательское соглашение
      </a>
      <button class="footer-link support-link" id="support-button" type="button" data-action="open-support">
        <span class="footer-icon" aria-hidden="true"><svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"></circle><path d="M9.5 9a2.5 2.5 0 1 1 3.5 2.3c-.9.4-1.3.9-1.3 1.7"></path><circle cx="12" cy="17.5" r="0.8" fill="currentColor" stroke="none"></circle></svg></span>
        Тех. поддержка
      </button>
      <div id="support-status" class="notification notification--info" role="status" aria-live="polite" hidden></div>
    </div>
    <button type="button" class="footer-toggle" id="menu-toggle" data-action="toggle-drawer" aria-label="Меню База Бизнеса" aria-expanded="${drawerOpen}">
      <img src="/baza-logo.png" alt="База Бизнеса" class="footer-logo">
      <span class="footer-title">База Бизнеса</span>
      <span class="footer-toggle-icon" aria-hidden="true"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4 7h16"></path><path d="M4 12h16"></path><path d="M4 17h16"></path></svg></span>
    </button>
  </div>`;
}

function renderEntityTab(type: EntityType): string {
  return `<button class="tab-button ui-tab${type === activeEntity ? ' active' : ''}"
    type="button" data-entity="${type}" role="tab" aria-selected="${type === activeEntity ? 'true' : 'false'}">
    ${ENTITY_CAPTIONS[type]}
  </button>`;
}

function renderPreset(name: string, label: string): string {
  return `<button class="btn btn-secondary ui-btn ui-btn-light preset-btn${activePreset === name ? ' active' : ''}"
    type="button" data-preset="${name}" ${disabledAttr()}>${escapeHtml(label)}</button>`;
}

function renderFields(): string {
  const selected = draft?.[activeEntity].fields || [];
  const ordered = draft?.[activeEntity].sort || [];
  const codes = visibleFieldCodes();

  if (!codes.length) {
    return '<div class="notification notification--info">По текущим условиям поля не найдены.</div>';
  }

  return `
    <div class="fields-table-shell">
      <div class="table-wrap">
        <table class="fields-table ui-table">
          <thead>
            <tr><th>Поле</th><th class="field-checkbox-cell">Отображать</th><th class="drag-cell-cell">Порядок</th></tr>
          </thead>
          <tbody>
            ${codes.map((code) => {
              const checked = selected.includes(code);
              const orderIndex = ordered.indexOf(code);
              return `<tr class="field-row${checked ? ' is-selected' : ''}">
                <td><div class="field-name-cell"><span>${escapeHtml(fieldTitle(activeEntity, code))}</span><span class="field-name-code">${escapeHtml(code)}</span></div></td>
                <td class="field-checkbox-cell">
                  <input class="field-checkbox" type="checkbox" data-field="${escapeHtml(code)}" ${checked ? 'checked' : ''} ${disabledAttr()}
                    aria-label="Показывать ${escapeHtml(fieldTitle(activeEntity, code))}">
                </td>
                <td class="drag-cell-cell">
                  <div class="drag-cell">
                    <button class="btn btn-secondary" type="button" data-move="${escapeHtml(code)}" data-direction="up"
                      aria-label="Поднять поле" ${!bootstrap?.isAdmin || !checked || orderIndex <= 0 ? 'disabled' : ''}>↑</button>
                    <button class="btn btn-secondary" type="button" data-move="${escapeHtml(code)}" data-direction="down"
                      aria-label="Опустить поле" ${!bootstrap?.isAdmin || !checked || orderIndex < 0 || orderIndex >= ordered.length - 1 ? 'disabled' : ''}>↓</button>
                  </div>
                </td>
              </tr>`;
            }).join('')}
          </tbody>
        </table>
      </div>
    </div>`;
}

function renderPreview(): string {
  const codes = draft?.[activeEntity].sort || [];
  if (!codes.length) {
    return '<div class="preview-empty"><strong>Поля не выбраны.</strong><br>Отметьте поля в таблице выше.</div>';
  }
  return `
    <div class="preview-widget">
      <div class="widget-tabs">
        ${entityTypes.map((type) => `<button class="widget-tab${type === activeEntity ? ' is-active' : ''}" type="button" data-entity="${type}">${ENTITY_CAPTIONS[type].replace(/ы$/, '').replace('Контакты', 'Контакт').replace('Компании', 'Компания').replace('Сделки', 'Сделка')}</button>`).join('')}
      </div>
      <div class="widget-panel is-active">
        <section class="widget-section">
          <div class="widget-section-head">
            <h4 class="widget-section-title">Информация о клиенте (${escapeHtml(ENTITY_CAPTIONS[activeEntity].replace(/ы$/, '').replace('Контакты', 'Контакт').replace('Компании', 'Компания').replace('Сделки', 'Сделка'))})</h4>
            <p class="widget-section-subtitle">Состав данных CRM по текущему звонку.</p>
          </div>
          <table class="info-table">
            <tbody>${codes.map((code) => `<tr><td>${escapeHtml(fieldTitle(activeEntity, code))}:</td><td><span class="field-value-main">—</span></td></tr>`).join('')}</tbody>
          </table>
        </section>
      </div>
    </div>`;
}

function renderCheckbox(name: string, label: string, checked: boolean): string {
  return `<div class="rule-item">
    <label class="feature-toggle">
      <input type="checkbox" data-setting="${name}" ${checked ? 'checked' : ''} ${disabledAttr()}>
      ${escapeHtml(label)}
    </label>
  </div>`;
}

function bindEvents(): void {
  root?.addEventListener('click', handleClick);
  root?.addEventListener('change', handleChange);
  root?.addEventListener('input', handleInput);
}

function handleClick(event: Event): void {
  const target = event.target as HTMLElement;
  const action = target.closest<HTMLElement>('[data-action]')?.dataset.action;
  if (drawerOpen && !target.closest('.footer-menu')) setBrandMenuOpen(false);
  if (action === 'toggle-drawer') {
    setBrandMenuOpen(!drawerOpen);
    return;
  }
  if (action === 'toggle-sidebar') {
    setMobileSidebarOpen(!mobileSidebarOpen);
    return;
  }
  if (action === 'close-sidebar') {
    setMobileSidebarOpen(false);
    return;
  }
  if (action === 'navigate-section') {
    const sectionId = target.closest<HTMLElement>('[data-section]')?.dataset.section;
    if (sectionId) setActiveMenuItem(sectionId);
    setMobileSidebarOpen(false);
    return;
  }
  if (action === 'open-support') {
    void openSupport();
    return;
  }
  if (action === 'select-visible' || action === 'clear-visible') {
    setVisibleFields(action === 'select-visible');
    return;
  }

  const entityButton = target.closest<HTMLButtonElement>('[data-entity]');
  if (entityButton?.dataset.entity) {
    activeEntity = entityButton.dataset.entity as EntityType;
    activePreset = '';
    searchValue = '';
    render();
    return;
  }
  const presetButton = target.closest<HTMLButtonElement>('[data-preset]');
  if (presetButton?.dataset.preset) {
    applyPreset(presetButton.dataset.preset);
    return;
  }
  const moveButton = target.closest<HTMLButtonElement>('[data-move]');
  if (moveButton?.dataset.move) {
    moveField(moveButton.dataset.move, moveButton.dataset.direction === 'up' ? -1 : 1);
    return;
  }
  if (target.closest('#save-settings')) void save();
  if (target.closest('#reload-settings')) window.location.reload();
}

function setBrandMenuOpen(open: boolean): void {
  drawerOpen = open;
  document.querySelector('.footer-menu')?.classList.toggle('is-open', drawerOpen);
  document.getElementById('menu-toggle')?.setAttribute('aria-expanded', String(drawerOpen));
}

function setMobileSidebarOpen(open: boolean): void {
  mobileSidebarOpen = open;
  document.body.classList.toggle('sidebar-open', mobileSidebarOpen);
  const button = document.getElementById('sidebar-toggle');
  button?.setAttribute('aria-expanded', String(mobileSidebarOpen));
  button?.setAttribute('aria-label', mobileSidebarOpen ? 'Закрыть меню' : 'Открыть меню');
}

function setActiveMenuItem(sectionId: string): void {
  document.querySelectorAll<HTMLElement>('.menu-item').forEach((item) => {
    item.classList.toggle('active', item.dataset.section === sectionId);
  });
}

async function openSupport(): Promise<void> {
  const button = document.getElementById('support-button') as HTMLButtonElement | null;
  const status = document.getElementById('support-status');
  const lineCode = bootstrap?.supportLineCode?.trim() || '';

  const showStatus = (message: string): void => {
    if (!status) return;
    status.textContent = message;
    status.hidden = message === '';
  };

  if (isLocalPreview()) {
    showStatus('Откройте приложение внутри Битрикс24.');
    return;
  }
  if (!lineCode) {
    showStatus('Линия технической поддержки не настроена.');
    return;
  }

  if (button) {
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
  }
  showStatus('Открываем чат поддержки…');

  try {
    const bx24 = await initBitrix();
    if (!bx24.callMethod || !bx24.im?.openMessenger) {
      throw new Error('Откройте приложение внутри Битрикс24.');
    }

    const callMethod = bx24.callMethod.bind(bx24);
    const openMessenger = bx24.im.openMessenger.bind(bx24.im);
    const dialogId = await new Promise<unknown>((resolve, reject) => {
      callMethod('imopenlines.network.join', { CODE: lineCode }, (result) => {
        const error = result.error?.();
        if (error) {
          console.error('Bitrix24 support line error', error);
          reject(new Error('Не удалось открыть техническую поддержку.'));
          return;
        }

        const data = result.data?.();
        if (data && typeof data === 'object' && 'result' in data) {
          resolve((data as { result?: unknown }).result);
          return;
        }
        resolve(data);
      });
    });

    if (dialogId === undefined || dialogId === null || dialogId === '') {
      throw new Error('Bitrix24 не вернул идентификатор чата.');
    }

    showStatus('');
    setBrandMenuOpen(false);
    openMessenger(String(dialogId));
  } catch (error) {
    showStatus(error instanceof Error ? error.message : 'Не удалось открыть техническую поддержку.');
  } finally {
    if (button) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }
}

function handleChange(event: Event): void {
  if (!draft) return;
  const input = event.target as HTMLInputElement;
  if (input.id === 'show-extra-descriptions') {
    showExtraDescriptions = input.checked;
    render();
    return;
  }
  if (input.id === 'show-system-fields') {
    showSystemFields = input.checked;
    render();
    return;
  }
  if (input.id === 'show-selected-only') {
    showSelectedOnly = input.checked;
    render();
    return;
  }

  const fieldCode = input.dataset.field;
  if (fieldCode) {
    const current = draft[activeEntity];
    if (input.checked) {
      current.fields = [...new Set([...current.fields, fieldCode])];
      current.sort = [...new Set([...current.sort, fieldCode])];
    } else {
      current.fields = current.fields.filter((code) => code !== fieldCode);
      current.sort = current.sort.filter((code) => code !== fieldCode);
    }
    activePreset = '';
    render();
    return;
  }

  const setting = input.dataset.setting;
  if (!setting) return;
  if (setting === 'merge_entities') {
    draft.__advanced.merge_entities = input.checked;
  } else {
    draft.__advanced.rules[setting as keyof CallCardSettings['__advanced']['rules']] = input.checked;
  }
  render();
}

function handleInput(event: Event): void {
  const input = event.target as HTMLInputElement;
  if (input.id !== 'field-search') return;
  searchValue = input.value;
  const cursor = input.selectionStart ?? searchValue.length;
  render();
  const nextInput = document.getElementById('field-search') as HTMLInputElement | null;
  nextInput?.focus();
  nextInput?.setSelectionRange(cursor, cursor);
}

function applyPreset(name: string): void {
  if (!draft || !bootstrap?.isAdmin) return;
  const preset = PRESETS[name]?.[activeEntity];
  if (!preset) return;
  const available = new Set(Object.keys(fieldMaps?.[activeEntity] || {}));
  const fields = preset.filter((code) => !available.size || available.has(code));
  draft[activeEntity] = { fields: [...fields], sort: [...fields] };
  if (isLocalPreview() && fieldMaps) {
    for (const code of fields) fieldMaps[activeEntity][code] ||= { title: code };
  }
  activePreset = name;
  render();
}

function setVisibleFields(selected: boolean): void {
  if (!draft || !bootstrap?.isAdmin) return;
  const codes = visibleFieldCodes();
  const current = draft[activeEntity];
  if (selected) {
    current.fields = [...new Set([...current.fields, ...codes])];
    current.sort = [...new Set([...current.sort, ...codes])];
  } else {
    const visible = new Set(codes);
    current.fields = current.fields.filter((code) => !visible.has(code));
    current.sort = current.sort.filter((code) => !visible.has(code));
  }
  activePreset = '';
  render();
}

function moveField(code: string, delta: number): void {
  if (!draft || !bootstrap?.isAdmin) return;
  const order = draft[activeEntity].sort;
  const index = order.indexOf(code);
  const next = index + delta;
  if (index < 0 || next < 0 || next >= order.length) return;
  [order[index], order[next]] = [order[next] as string, order[index] as string];
  activePreset = '';
  render();
}

async function save(): Promise<void> {
  if (!draft || saving || !bootstrap?.isAdmin) return;
  saving = true;
  const button = document.getElementById('save-settings') as HTMLButtonElement | null;
  if (button) {
    button.disabled = true;
    button.textContent = 'Сохраняем…';
  }
  try {
    const result = await apiFetch<{ settings: CallCardSettings }>('/api/v1/settings', {
      method: 'PUT',
      body: JSON.stringify(draft)
    });
    draft = normalizeSettings(result.settings);
    savedSnapshot = snapshot(draft);
    render();
    showNotice('Настройки успешно сохранены.', 'success');
  } catch (error) {
    render();
    showNotice(error instanceof Error ? error.message : 'Не удалось сохранить настройки', 'warning');
  } finally {
    saving = false;
    resizeFrame();
  }
}

function showNotice(message: string, type: 'success' | 'warning'): void {
  const notice = document.getElementById('notice');
  if (notice) notice.innerHTML = `<div class="notification notification--${type === 'success' ? 'success' : 'error'}">${escapeHtml(message)}</div>`;
}

function visibleFieldCodes(): string[] {
  const selected = draft?.[activeEntity].fields || [];
  const ordered = draft?.[activeEntity].sort || [];
  const mapCodes = Object.keys(fieldMaps?.[activeEntity] || {});
  return [...new Set([...ordered, ...mapCodes])]
    .filter((code) => showSystemFields || !isSystemField(code))
    .filter((code) => !showSelectedOnly || selected.includes(code))
    .filter((code) => {
      const query = searchValue.trim().toLowerCase();
      return !query || code.toLowerCase().includes(query) || fieldTitle(activeEntity, code).toLowerCase().includes(query);
    })
    .sort((left, right) => {
      const leftIndex = ordered.indexOf(left);
      const rightIndex = ordered.indexOf(right);
      if (leftIndex >= 0 || rightIndex >= 0) {
        if (leftIndex < 0) return 1;
        if (rightIndex < 0) return -1;
        return leftIndex - rightIndex;
      }
      return fieldTitle(activeEntity, left).localeCompare(fieldTitle(activeEntity, right), 'ru');
    });
}

function fieldTitle(type: EntityType, code: string): string {
  const definition = fieldMaps?.[type]?.[code];
  return definition?.formLabel || definition?.listLabel || definition?.filterLabel || definition?.title || code;
}

function createLocalFieldMaps(settings: CallCardSettings): FieldMaps {
  return Object.fromEntries(entityTypes.map((type) => [
    type,
    Object.fromEntries(settings[type].sort.map((code) => [code, { title: code }]))
  ])) as FieldMaps;
}

function isLocalPreview(): boolean {
  return bootstrap?.memberId === 'local-preview';
}

function disabledAttr(): string {
  return bootstrap?.isAdmin ? '' : 'disabled';
}

function isDirty(): boolean {
  return Boolean(draft && savedSnapshot && snapshot(draft) !== savedSnapshot);
}

function snapshot(value: CallCardSettings): string {
  return JSON.stringify(normalizeSettings(structuredClone(value)));
}

function isSystemField(code: string): boolean {
  return [
    /^ID$/,
    /^LINK$/,
    /^DATE_CREATE$/,
    /^DATE_MODIFY$/,
    /^MOVED_/,
    /^LAST_ACTIVITY_/,
    /^LAST_COMMUNICATION_/,
    /^IS_/,
    /^CREATED_BY_ID$/,
    /^MODIFY_BY_ID$/,
    /^OPENED$/,
    /^ORIGINATOR_ID$/,
    /^ORIGIN_ID$/,
    /^SEARCH_CONTENT$/,
    /^UTM_/,
    /^ADDRESS_.*_CODE$/,
    /^CONTACT_IDS?$/,
    /^COMPANY_IDS?$/
  ].some((pattern) => pattern.test(code));
}
